import {
  type PublicProfile,
  toAffiliations,
  QuizResponsePreviewBase,
} from "@core/features/publicProfile";
import { Sheet } from "@themes/pollinator/ui/Sheet";
import { Type } from "@themes/pollinator/ui/Type";
import { Image } from "@themes/pollinator/ui/Image";
import { Loader } from "@themes/pollinator/ui/Loader";
import { ProfileHeader } from "@themes/pollinator/ui/ProfileHeader";
import { ProfileSection } from "@themes/pollinator/ui/ProfileSection";
import { ProfileAffiliationList } from "@themes/pollinator/ui/ProfileAffiliationList";

const AUDIO_EXTENSIONS = /\.(mp3|wav|ogg|webm|aac|m4a)(\?|$)/i;

export type Props = {
  /** Whether the sheet is open */
  isOpen: boolean;
  /** Callback to close the sheet */
  onClose: () => void;
  /** Profile data (null while loading) */
  profile: PublicProfile | null;
  /** Whether the profile data is loading */
  isLoading: boolean;
  /** Error message if fetch failed */
  error?: string | null;
};

/**
 * Public profile sheet component.
 * Displays user's public profile information in a sliding sheet.
 */
export function PublicProfileSheet({
  isOpen,
  onClose,
  profile,
  isLoading,
  error,
}: Props) {
  // Use the first profile quiz response if available
  const quizResponse = profile?.profile_quiz_responses?.[0] ?? null;
  const questions = quizResponse?.quiz?.questions ?? [];

  // Move representative image to the end
  const sortedQuestions = [...questions].sort((a, b) => {
    if (a.internal_name === "profile_image") return 1;
    if (b.internal_name === "profile_image") return -1;
    return 0;
  });

  // Get profile data from user profile fields
  const profileImage = profile?.user_info.profile_photo;
  const pronouns = profile?.user_info.pronouns;

  // Get full name
  const fullName = profile
    ? `${profile.user_info.first_name} ${profile.user_info.last_name}`
    : "";

  // Convert affiliations
  const affiliations = toAffiliations(profile?.affiliations);

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      aria-label="Public profile preview"
      className="max-w-[360px] overflow-hidden border-l border-brand-800"
      innerClassName="justify-start overflow-auto"
      data-testid="public-profile-sheet"
    >
      {isLoading && (
        <div className="flex items-center justify-center h-full">
          <Loader isFullPage />
        </div>
      )}

      {error && (
        <div className="flex items-center justify-center h-full p-6">
          <Type variant="body2" className="text-red-500">
            {error}
          </Type>
        </div>
      )}

      {profile && !isLoading && !error && (
        <div className="flex flex-col">
          {/* Header with avatar and name */}
          <ProfileHeader
            image={profileImage ?? undefined}
            name={fullName}
            pronouns={pronouns ?? undefined}
            data-testid="public-profile"
          />

          {/* Content sections */}
          <div className="flex flex-col gap-6 p-6">
            {/* Quiz response answers */}
            {sortedQuestions.length > 0 && (
              <QuizResponsePreviewBase
                questions={sortedQuestions}
                hideUnanswered
                className="flex flex-col gap-6"
                data-testid="public-profile-quiz-answers"
                renderItem={({ answer }) => (
                  <ProfileSection
                    label={answer.label}
                    gap={answer.uploadedMedia?.length ? 4 : undefined}
                  >
                    {/* Text answer */}
                    {answer.textValue && (
                      answer.type === "url" ? (
                        <a
                          href={
                            answer.textValue.startsWith("http")
                              ? answer.textValue
                              : `https://${answer.textValue}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-brand-700 transition-colors"
                        >
                          <Type variant="caption" className="underline">
                            {answer.textValue}
                          </Type>
                        </a>
                      ) : (
                        <Type variant="caption" className="whitespace-pre-wrap">
                          {answer.textValue}
                        </Type>
                      )
                    )}

                    {/* Selected choices */}
                    {answer.selectedChoices && answer.selectedChoices.length > 0 && (
                      <Type variant="caption">
                        {answer.selectedChoices.join(", ")}
                      </Type>
                    )}

                    {/* Uploaded media */}
                    {answer.uploadedMedia && answer.uploadedMedia.length > 0 && (
                      <div className="flex flex-col gap-2">
                        {answer.uploadedMedia.map((media) =>
                          AUDIO_EXTENSIONS.test(media.file) ? (
                            <a
                              key={media.id}
                              href={media.file}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline hover:text-brand-700 transition-colors"
                            >
                              <Type variant="caption">Listen</Type>
                            </a>
                          ) : (
                            <a
                              key={media.id}
                              href={media.file}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <Image
                                src={media.file}
                                alt={media.alt_text || answer.label}
                                className="w-full rounded-sm overflow-hidden border-0"
                                imageClassName="object-cover"
                              />
                            </a>
                          )
                        )}
                      </div>
                    )}
                  </ProfileSection>
                )}
              />
            )}

            {/* Affiliations */}
            {affiliations.length > 0 && (
              <ProfileAffiliationList
                affiliations={affiliations}
                data-testid="public-profile-affiliations"
              />
            )}
          </div>
        </div>
      )}
    </Sheet>
  );
}

export default PublicProfileSheet;
